import React, { useContext, useState } from 'react';

import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Link } from '@uhg-abyss/web/ui/Link';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { Card } from '@uhg-abyss/web/ui/Card';
import { Button } from '@uhg-abyss/web/ui/Button';
import { Checkbox } from '@uhg-abyss/web/ui/Checkbox';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { cellValueHighLight } from '@src/utils/tableUtils';
import { getEnvironment, getServiceURL } from '@src/env.config';
import {
  useLazyGetReportsQuery,
  useLazySetCustomerCorrChangeOwnerQuery,
} from '@src/store/services/cdb/onlineServices';
import {
  useLazyGetGPSReportsQuery,
  useLazyGetGpsENWChildReportsQuery,
} from '@src/store/services/gps/onlineServices';
import CryptoJS from 'crypto-js';
import { encdata } from '@src/constants';

import { FileContext } from '@src/routes/Routes';
import { Table } from '@src/common/Table';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useAuth } from '@src/Providers/AuthProvider';
import { maskUsingUserAccess } from '@src/utils';

export const SubmitterCorrections = ({ procStatus }) => {
  const [transactionData, setTransactionData] = useState<
    Record<string, string>[]
  >([]);
  const [trigger] = useLazySetCustomerCorrChangeOwnerQuery();
  const { user } = useAuth();
  const router = useRouter();
  const { service, setId, fieldCode, errorCode } = router.getRouteParams();
  const distinationSystem = service === 'gps' ? '/gps' : 'cdb';

  const [isExtType, setIsExtType] = useState(
    user?.userType === 'E' ? true : false
  );
  const [isTableLoading, setIsTableLoading] = useState(false);

  const { fileData } = useContext(FileContext);
  const errorStatuses = [
    'COMPLETED_WITH_ERRORS',
    'COMPLETED_WITH_WARNINGS',
    'CMPL_REPR_ALL_WITH_ERR',
    'CMPL_REPR_PART_WITH_ERR',
    'CMPL REPR ALL WITH WRN',
    'CMPL REPR PART WITH WRN',
  ];

  const isCompletedWithErr = errorStatuses.some((status) =>
    procStatus?.includes(status)
  );
  const handleTransaction = (row, isSelected) => {
    const base = {
      fileId: row?.transFileId ?? fileData?.fileId ?? fileData?.fileID,
      transSetId: row?.transSetId ?? fileData?.transSetId ?? setId,
      distinationSystem,
    };
    setTransactionData((prev) =>
      !isSelected
        ? prev.some((t) => t.xcptId === row?.xcptId)
          ? prev
          : [
              ...prev,
              {
                ...base,
                transId: row?.transId,
                xcptId: row?.xcptId,
                employeeId: row?.employeeId,
                action: 'CHANGE_OWNER',
              },
            ]
        : prev.filter((t) => t.xcptId !== row?.xcptId)
    );
  };
  const NameLink = ({ name }) => {
    const url = `${getServiceURL()}/home/help/custCorrecFieldDef#${name}`;

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: 'white' }}
      >
        {name}
      </a>
    );
  };
  const columns = React.useMemo(() => {
    return [
      {
        Header: <NameLink name="File Processing Date" />,
        accessor: service === 'gps' ? 'fileProcessingDate' : 'processingDt',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 80,
        width: 80,

        Cell: ({ row, value }) => {
          const processedValue = value.split(' ')[0];

          return cellValueHighLight({
            value: processedValue,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Severity" />,
        accessor: 'severityCode',
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        minWidth: 50,
        width: 50,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Employee ID" />,
        accessor: 'employeeId',
        canToggleVisibilty: false,

        isHiddenByDefault: service === 'gps' ? true : false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Medicare ID" />,
        accessor: 'medicareId',
        canToggleVisibilty: false,
        minWidth: 85,
        width: 85,
        isHiddenByDefault: service === 'gps' ? false : true,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: 'Family ID',
        accessor: 'familyId',
        canToggleVisibilty: false,
        minWidth: 60,
        width: 60,
        isHiddenByDefault: service === 'gps' ? false : true,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
          });
        },
      },
      {
        Header: <NameLink name="Name" />,
        accessor: 'name',
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        minWidth: 100,
        width: 100,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Status" />,
        accessor: 'status',
        canToggleVisibilty: false,
        isHiddenByDefault: false,
        minWidth: 110,
        width: 110,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Rel" />,
        accessor: service === 'gps' ? 'mbrTicRelCd' : 'rel',
        canToggleVisibilty: true,
        isHiddenByDefault: service === 'gps' ? true : false,
        minWidth: 50,
        width: 50,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Coverage Type" />,
        accessor: 'covType',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 60,
        width: 60,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Type" />,
        accessor: 'type',
        minWidth: 80,
        width: 80,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Transaction Source" />,
        accessor: 'transactionSource',
        minWidth: 75,
        width: 75,
        canToggleVisibilty: true,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
        isHiddenByDefault: service === 'gps' ? true : false,
      },
      {
        Header: <NameLink name="Error Name" />,
        accessor: 'warningName',
        minWidth: 75,
        width: 75,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Data field" />,
        accessor: 'dataField',
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        minWidth: 95,
        width: 95,
        Cell: ({ row, value }) => {
          return cellValueHighLight({
            value,
            fileData,
            typeValue: row?.original?.multiTransId,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Customer data" />,
        accessor: service === 'gps' ? 'customerData' : 'custData',
        minWidth: 100,
        width: 100,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          const maskedValue = maskUsingUserAccess(
            value,
            row?.original?.dataField || '',
            user?.maskingType || 0
          );

          const typeValue = row?.original?.multiTransId;
          return cellValueHighLight({
            value: maskedValue,
            fileData,
            typeValue,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      {
        Header: <NameLink name="Source Data" />,
        accessor: 'sourceData',
        minWidth: 100,
        width: 100,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        Cell: ({ row, value }) => {
          const maskedValue = maskUsingUserAccess(
            value,
            row?.original?.dataField || '',
            user?.maskingType || 0
          );

          const typeValue = row?.original?.multiTransId;
          return cellValueHighLight({
            value: maskedValue,
            fileData,
            typeValue,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
      ...(isCompletedWithErr && !isExtType
        ? [
            {
              Header: 'Change Owner',
              accessor: 'changeOwner',
              width: 55,
              minWidth: 55,
              Cell: ({ row }) => {
                const [isChecked, setIsChecked] = useState(false);
                const handleChangeOwner = (e) => {
                  handleTransaction(row.original, !e.target.checked);
                  setIsChecked(e.target.checked);
                };

                const typeValue = row?.original?.multiTransId?.trim();
                const value = (
                  <Layout.Group>
                    <Checkbox
                      label=""
                      name={`changeOwner-${row.original.id}`}
                      isChecked={isChecked}
                      onChange={handleChangeOwner}
                    />
                  </Layout.Group>
                );

                return cellValueHighLight({
                  value,
                  fileData,
                  typeValue,
                  changeowner: row?.original?.xcptOwenerChgCd,
                });
              },
            },
          ]
        : []),
      {
        Header: 'Detail View',
        accessor: 'detailView',
        enableSorting: false,
        canToggleVisibilty: true,
        isHiddenByDefault: false,
        label: 'Customer Name',
        width: 80,
        minWidth: 80,
        Cell: ({ row }) => {
          const nameSplit = row?.original?.name.split(',');
          const firstName = nameSplit[1].trim();
          const lastName = nameSplit[0].trim();
          const typeValue = row?.original?.multiTransId?.trim();
          const value =
            service !== 'gps' &&
            fileData?.multilevelSubmitter &&
            typeValue?.length > 0
              ? 'View'
              : 'Detail View';

          const link =
            service === 'gps'
              ? `/home/gps-detail-view/`
              : fileData?.multilevelSubmitter && typeValue?.length > 0
              ? `/home/multi-level-view/`
              : `/home/detail-view/`;

          const linkData = {
            fileID: fileData?.fileID || fileData.fileId,
            transSetId: fileData?.transSetId,
            medicareId: row?.original?.medicareId,
            firstName: firstName,
            lastName: lastName.trim(),
            processingDt: fileData?.processingDt,
            customerName: fileData?.customerName,
            mbrTicRelCd: row?.original?.mbrTicRelCd,
            transId: row?.original?.transId,
            subscrId: row?.original?.subscrId,
            sbmtGrpName: fileData?.sbmtGrpName,
            relationship: row?.original?.rel,
            cescustnbr: fileData?.cescustnbr,
            sbmtGrpId: fileData?.sbmtGrpId,
            reportType: 'EE',
            empId: row?.original?.employeeId,
          };

          const encryptedData = CryptoJS.AES.encrypt(
            JSON.stringify(linkData),
            encdata
          ).toString();

          const newUrl = `${link}${encodeURIComponent(encryptedData)}`;

          return cellValueHighLight({
            value: (
              <Flex
                css={{
                  gap: '10px',
                }}
              >
                <Link
                  size="$sm"
                  target="_blank"
                  href={newUrl}
                  onClick={(e) => {
                    e.preventDefault();
                    window.open(newUrl, 'DetailViewTab');
                  }}
                >
                  <Button
                    variant="outline"
                    size="$sm"
                    css={{
                      fontSize: '12px',
                    }}
                  >
                    <strong>{value}</strong>
                  </Button>
                </Link>
              </Flex>
            ),
            fileData,
            typeValue,
            changeowner: row?.original?.xcptOwenerChgCd,
          });
        },
      },
    ];
  }, [isCompletedWithErr, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await trigger(transactionData).unwrap(); // pass array directly
      router.navigate('/');
    } catch (err) {
      console.error('submit error', err);
    }
  };

  return (
    <Card
      css={{
        'abyss-card-root': { width: '100%', padding: '0', borderRadius: '4px' },
      }}
    >
      <div
        style={{
          backgroundColor:
            'var(--abyss-colors-web-semantic-color-surface-container-primary)',
          color:
            'var(--abyss-colors-web-semantic-color-text-content-primaryAlt)',
          fontSize: '18px',
          padding: '24px',
          borderTopLeftRadius: '3px',
          borderTopRightRadius: '3px',
          fontWeight: 'bold',
        }}
      >
        Customer Corrections Required
      </div>
      <br />
      <p style={{ fontSize: '14px' }}>
        The information presented here only reflects changes made to your
        company's master eligibility file and processed through the Electronic
        Eligibility Management System. It does not reflect changes made through
        other sources (e.g., EmployereServices.com, paper eligibility forms,
        customer service requests). Please remember to always update your
        eligibility source system. &nbsp;You may view your current eligibility
        information online at &nbsp;
        <a
          href={`${getServiceURL()}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {getServiceURL()}
        </a>
        &nbsp; If you have questions, please contact the Customer Service Center
        Help Desk at 1.800.651.5465.
      </p>

      <div style={{ padding: '16px' }}>
        <Table
          columns={columns}
          dataKey="fileListTable"
          fetch={
            service === 'gps'
              ? fieldCode && fieldCode.length > 1
                ? useLazyGetGpsENWChildReportsQuery
                : useLazyGetGPSReportsQuery
              : useLazyGetReportsQuery
          }
          requestParams={{
            ...router.getRouteParams(),
            ...fileData,
          }}
          customHeaderSection={
            isCompletedWithErr ? (
              <form onSubmit={handleSubmit}>
                <Layout.Group
                  css={{
                    gap: '20px',
                    alignItems: 'flex-end',
                    '.abyss-select-input-root': {
                      minWidth: '288px',
                    },
                  }}
                >
                  {/* {fileData?.allDate && fileData?.allDate === 'true' ? (
                    <SelectInput
                      label="File Processing Date"
                      placeholder=""
                      value=""
                      onChange={(value) => {}}
                      options={[{ label: '----', value: '' }]}
                    />
                  ) : null} */}
                  {user?.cmplRepr ? (
                    <React.Fragment>
                      <Button
                        size="$sm"
                        type="submit"
                        isDisabled={isTableLoading}
                      >
                        Submit
                      </Button>
                      <Button
                        size="$sm"
                        variant="filled"
                        color="destructive"
                        isDisabled={isTableLoading}
                        style={{ marginLeft: '8px' }}
                      >
                        clear
                      </Button>
                    </React.Fragment>
                  ) : null}
                </Layout.Group>
              </form>
            ) : null
          }
          showGlobalFilter={true}
          onLoadingChange={setIsTableLoading}
          downloadMenuItems={[
            {
              title: 'Download CSV',
              onClick: 'exportVisibleData',
              csvFilename: `submitter-corrections-${service}.csv`,
            },
          ]}
        />
      </div>
    </Card>
  );
};
